import { Link, useLocation, useParams } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  FileText,
  HelpCircle,
  Inbox,
  ListChecks,
  Package,
  ScrollText,
  ShieldCheck,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";

const COLLAPSED_KEY = "sp-nav-collapsed";

interface NavItem {
  to: string;
  label: string;
  icon: typeof Inbox;
  count?: number;
}

type SolutionSidebarProps = Record<string, never>;

export function SolutionSidebar(_: SolutionSidebarProps) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(COLLAPSED_KEY) === "1";
  });
  const { solutionId } = useParams({ from: "/s/$solutionId" });
  const location = useLocation();
  const { data: session } = useSession();
  const isAdmin = session?.user?.role === "admin";

  useEffect(() => {
    window.localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  const base = `/s/${solutionId}`;

  const primary: NavItem[] = [
    { to: `${base}/intake`, label: "Intake", icon: Inbox },
    { to: `${base}/review`, label: "Review", icon: ListChecks },
    { to: `${base}/scope`, label: "Scope", icon: ScrollText },
  ];

  const secondary: NavItem[] = [
    { to: `${base}/questions`, label: "Questions", icon: HelpCircle },
    { to: `${base}/report`, label: "Report", icon: FileText },
    { to: `${base}/members`, label: "Members", icon: Users },
  ];

  const comingSoon: NavItem[] = [
    { to: `${base}/followups`, label: "Follow-ups", icon: CheckSquare },
    { to: `${base}/conflicts`, label: "Conflicts", icon: AlertTriangle },
  ];

  const tertiary: NavItem[] = [
    { to: "/solutions", label: "All solutions", icon: Package },
    ...(isAdmin
      ? [{ to: "/dashboard/settings/users", label: "Users", icon: ShieldCheck } as NavItem]
      : []),
  ];

  return (
    <aside
      className={cn(
        "sticky top-0 h-screen flex flex-col border-r border-[var(--color-border)] bg-[var(--color-card)] transition-[width] duration-200",
        collapsed ? "w-16" : "w-[232px]",
      )}
    >
      <div className="flex items-center gap-2 px-3 h-[58px] border-b border-[var(--color-border)]">
        <div
          className="h-7 w-7 rounded-md shrink-0 bg-nymbl-navy text-white flex items-center justify-center font-display font-bold text-xs"
          aria-hidden
        >
          N
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-sm text-nymbl-navy truncate">
              Scope POC
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="text-muted-foreground hover:text-foreground shrink-0 p-1 rounded-sm hover:bg-muted"
          title={collapsed ? "Expand nav" : "Collapse nav"}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-2 flex flex-col">
        <NavGroup items={primary} pathname={location.pathname} collapsed={collapsed} />
        <div className="mx-3 my-2 h-px bg-[var(--color-border)]" />
        <NavGroup items={secondary} pathname={location.pathname} collapsed={collapsed} />
        <div className="mx-3 my-2 h-px bg-[var(--color-border)]" />
        {!collapsed && (
          <div className="px-5 pt-1 pb-1.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
            Coming soon
          </div>
        )}
        <NavGroup items={comingSoon} pathname={location.pathname} collapsed={collapsed} muted />
        <div className="mt-auto">
          <div className="mx-3 mb-2 h-px bg-[var(--color-border)]" />
          <NavGroup items={tertiary} pathname={location.pathname} collapsed={collapsed} />
        </div>
      </nav>
    </aside>
  );
}

function NavGroup({
  items,
  pathname,
  collapsed,
  muted = false,
}: {
  items: NavItem[];
  pathname: string;
  collapsed: boolean;
  muted?: boolean;
}) {
  return (
    <ul className="px-2 space-y-0.5">
      {items.map((item) => {
        const Icon = item.icon;
        const isActive =
          item.to === "/solutions"
            ? pathname === "/solutions"
            : pathname === item.to || pathname.startsWith(`${item.to}/`);

        return (
          <li key={item.to}>
            <Link
              to={item.to}
              className={cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-nymbl-sky text-nymbl-navy"
                  : muted
                    ? "text-muted-foreground/70 hover:bg-muted hover:text-foreground"
                    : "text-foreground/70 hover:bg-muted hover:text-foreground",
                collapsed && "justify-center px-2",
              )}
              title={collapsed ? item.label : undefined}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
              {!collapsed && item.count != null && item.count > 0 && (
                <span
                  className={cn(
                    "rounded-full text-[10px] font-semibold px-1.5 py-0.5 min-w-[18px] text-center",
                    isActive ? "bg-nymbl-navy text-white" : "bg-muted text-foreground/70",
                  )}
                >
                  {item.count}
                </span>
              )}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
